import { createFileRoute } from "@tanstack/react-router";
import { Phone, MapPin, ChevronRight, Check } from "lucide-react";
import { openQuoteModal } from "../components/quote-modal";
import { Reveal } from "../components/reveal";
import { Breadcrumbs } from "../components/breadcrumbs";
import { NAV_CITIES } from "../data/nav-items";
import { InlineQuoteSection } from "@/components/inline-quote-section";

const SITE = "https://capitalpropressurewashing.com";
const TITLE = "Service Area | Pressure Washing Capital Region NY | Capital Pro";
const DESC =
  "Capital Pro Pressure Washing serves Albany, Schenectady, Saratoga Springs, Troy, Clifton Park, Colonie, Latham, Guilderland, Delmar, Niskayuna, Ballston Spa, Rensselaer, and the surrounding Capital Region, NY.";

const SERVICES_OFFERED = [
  "House Washing",
  "Roof Washing",
  "Concrete & Driveway Cleaning",
  "Paver Cleaning & Sealing",
  "Deck & Patio Cleaning",
  "Gutter Cleaning",
  "Window Cleaning",
  "Commercial Pressure Washing",
];

const REGIONS = [
  {
    heading: "Albany County",
    towns: "Albany, Colonie, Latham, Guilderland, Delmar, Bethlehem, Glenmont, Loudonville, Menands, Altamont, Voorheesville, Ravena",
    body:
      "The densest part of our route. Older neighborhoods in Albany's Pine Hills and Delaware Avenue have mature tree cover, which means heavy algae on north-facing siding and clogged gutters every fall. Newer builds in Guilderland and Bethlehem tend to need driveway and paver work more than anything else. We're in Albany County almost daily, so same-week scheduling is normal here.",
  },
  {
    heading: "Saratoga County",
    towns: "Saratoga Springs, Clifton Park, Ballston Spa, Halfmoon, Malta, Round Lake, Waterford, Mechanicville",
    body:
      "Saratoga Springs has a large stock of historic homes where soft washing is the only safe option — no high pressure near old wood, porch trim, or original clapboard. Clifton Park and Halfmoon are dominated by 1990s and 2000s subdivisions with vinyl siding, big decks, and paver patios that need sand-and-seal every few years. Northway access makes this a fast run for us.",
  },
  {
    heading: "Schenectady County",
    towns: "Schenectady, Niskayuna, Rotterdam, Scotia, Glenville, Duanesburg",
    body:
      "Schenectady's Stockade and GE Realty Plot are full of brick and slate that need a careful, low-pressure hand. Niskayuna and Glenville homes back onto heavy tree lines, which drives our two biggest jobs there: roof washing for black streaks and gutter cleaning after leaf drop.",
  },
  {
    heading: "Rensselaer County",
    towns: "Troy, East Greenbush, Rensselaer, North Greenbush, Wynantskill, Brunswick, Averill Park",
    body:
      "Troy's row houses and brownstones sit close to the road and collect road grime and salt spray all winter. Out toward East Greenbush and Averill Park we see more open lots, longer driveways, and larger decks — the concrete and deck side of our work.",
  },
];

const FAQS = [
  {
    q: "What towns do you serve in the Capital Region?",
    a: "We cover Albany, Schenectady, Troy, Saratoga Springs, Clifton Park, Colonie, Latham, Guilderland, Delmar, Bethlehem, Niskayuna, Ballston Spa, Rensselaer, East Greenbush, Halfmoon, Glenmont, Loudonville, Menands, Altamont, Ravena, and the surrounding towns across Albany, Saratoga, Schenectady, and Rensselaer counties.",
  },
  {
    q: "Do you charge a travel or trip fee?",
    a: "No trip fee anywhere in our standard service area. For jobs well outside it we'll tell you before we quote rather than adding it at the end — and in most cases we can still get to you.",
  },
  {
    q: "My town isn't listed. Can you still come out?",
    a: "Usually yes. The list covers where we work most often, not a hard boundary. Call (518) 900-1913 with your address and we'll confirm coverage in about a minute.",
  },
  {
    q: "How fast can you get to my property?",
    a: "Most quotes come back the same day, and scheduling is typically within a week during the season. Spring and early fall are our busiest stretches, so booking a week or two ahead gets you the date you want.",
  },
  {
    q: "Do you have a storefront I can visit?",
    a: "We're a mobile service based in Albany, NY — no retail location. Everything happens at your property, and quotes are handled by phone, online, or with an on-site walkthrough at no charge.",
  },
  {
    q: "Do you need to visit before quoting?",
    a: "Usually not. We can measure and price most house washes, roofs, and driveways remotely using satellite imagery. On-site quotes are always free if you'd rather walk the property together.",
  },
  {
    q: "Do you serve commercial properties across the whole region?",
    a: "Yes. Storefronts, HOAs, restaurants, medical offices, and multi-site portfolios across all four counties, with Certificates of Insurance issued before the first visit and after-hours scheduling available.",
  },
  {
    q: "What's the best time of year for exterior cleaning in upstate New York?",
    a: "April through November. Spring is ideal for washing off winter salt and pollen; fall is best for gutters and pre-winter concrete sealing before the freeze-thaw cycle starts. We work right through the summer as well.",
  },
];

export const Route = createFileRoute("/service-area")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}/service-area` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: `${SITE}/service-area` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "Capital Pro Pressure Washing",
          url: SITE,
          telephone: "+15189001913",
          priceRange: "$$",
          image: `${SITE}/__l5e/assets-v1/7fb348af-f23e-48db-88cd-6aeda94adc1a/hero-crew-surface-cleaner.jpg`,
          areaServed: NAV_CITIES.map((c) => c.city),
          address: {
            "@type": "PostalAddress",
            streetAddress: "5A Vatrano Rd",
            addressLocality: "Albany",
            addressRegion: "NY",
            postalCode: "12205",
            addressCountry: "US",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: ServiceAreaPage,
});

function ServiceAreaPage() {
  return (
    <main>
      <Breadcrumbs items={[{ label: "Service Area" }]} />

      {/* Hero */}
      <section className="bg-[var(--brand-deep-blue)] text-white py-14 md:py-20">
        <div className="mx-auto max-w-[1200px] px-4 text-center">
          <p
            className="font-semibold uppercase tracking-[0.2em] text-sm mb-3"
            style={{ fontFamily: "var(--font-display)", color: "#ff6b7f" }}
          >
            Capital Region, New York
          </p>
          <h1
            className="text-3xl md:text-5xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Where We Work
          </h1>
          <p className="mt-4 text-white/85 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            We proudly serve homeowners and businesses across the Capital Region. From Albany
            to Saratoga Springs, if you're in our area, we'll get you a free quote — often the
            same day.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => openQuoteModal()}
              className="inline-flex items-center justify-center rounded-md bg-[#C8102E] text-white px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Check Your Address
            </button>
            <a
              href="tel:+15189001913"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-[var(--brand-deep-blue)] px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              <Phone size={18} strokeWidth={2.5} />
              Call (518) 900-1913
            </a>
          </div>
        </div>
      </section>

      {/* City grid */}
      <section className="bg-white py-14 md:py-16">
        <div className="mx-auto max-w-[1000px] px-4">
          <div className="text-center mb-8 md:mb-10">
            <p
              className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-2"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Cities & Towns We Serve
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              {NAV_CITIES.length}+ Capital Region Communities
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {NAV_CITIES.map((city, i) => (
              <Reveal
                key={city.slug}
                delayMs={i * 40}
                className="card-pop"
              >
                <a
                  href={`/${city.slug}`}
                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-[var(--brand-light-gray)] p-4 hover:border-[var(--brand-deep-blue)] hover:bg-white transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-[var(--brand-deep-blue)]/10 text-[var(--brand-deep-blue)] flex items-center justify-center shrink-0">
                      <MapPin size={20} />
                    </div>
                    <div>
                      <span
                        className="text-[var(--brand-deep-blue)] text-base font-bold uppercase leading-tight"
                        style={{ fontFamily: "var(--font-display)" }}
                      >
                        {city.city}
                      </span>
                      <p className="text-[var(--brand-charcoal)]/60 text-xs">Pressure Washing</p>
                    </div>
                  </div>
                  <ChevronRight
                    size={20}
                    className="text-[var(--brand-charcoal)]/40 group-hover:text-[var(--brand-red)] transition-colors shrink-0"
                  />
                </a>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 text-center text-[var(--brand-charcoal)]/70 text-sm md:text-base">
            Don't see your town? We serve the entire Capital Region and surrounding areas.
            {" "}
            <a
              href="tel:+15189001913"
              className="text-[var(--brand-deep-blue)] font-semibold hover:text-[var(--brand-bright-blue)]"
            >
              Give us a call
            </a>{" "}
            and ask.
          </p>
        </div>
      </section>

      {/* What we offer in these areas */}
      <section className="bg-[var(--brand-light-gray)] py-14 md:py-16">
        <div className="mx-auto max-w-[900px] px-4">
          <div className="text-center mb-8">
            <p
              className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-2"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Available Across the Region
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Every Service, Every Town
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SERVICES_OFFERED.map((s) => (
              <div
                key={s}
                className="flex items-center gap-3 bg-white rounded-lg border border-gray-200 p-4"
              >
                <Check size={18} strokeWidth={3} className="text-[var(--brand-red)] shrink-0" />
                <span className="text-[var(--brand-charcoal)] text-sm md:text-base font-medium">
                  {s}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* County-by-county local context */}
      <section className="bg-white py-14 md:py-16">
        <div className="mx-auto max-w-[1000px] px-4">
          <div className="text-center mb-8">
            <p
              className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-2"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Four Counties, One Local Crew
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Where We Work &amp; What We See There
            </h2>
            <p className="mt-3 text-[var(--brand-charcoal)]/75 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
              We've cleaned exteriors across the Capital Region for over 10 years. Every part of the
              area has its own mix of housing stock, tree cover, and weather exposure — here's what
              that means for your property.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {REGIONS.map((r, i) => (
              <Reveal key={r.heading} delayMs={i * 60} className="card-pop">
                <div className="h-full rounded-lg border border-gray-200 bg-[var(--brand-light-gray)] p-6">
                  <h3
                    className="text-[var(--brand-deep-blue)] text-xl uppercase"
                    style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                  >
                    {r.heading}
                  </h3>
                  <p className="mt-1 text-[var(--brand-charcoal)]/60 text-xs">{r.towns}</p>
                  <p className="mt-3 text-[var(--brand-charcoal)]/80 text-sm leading-relaxed">{r.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How service in your town works */}
      <section className="bg-[var(--brand-light-gray)] py-14 md:py-16 border-t border-gray-200">
        <div className="mx-auto max-w-[900px] px-4">
          <h2
            className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight text-center"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            How Service Works In Your Town
          </h2>
          <div className="mt-8 space-y-5 text-[var(--brand-charcoal)]/80 text-sm md:text-base leading-relaxed">
            <p>
              We're a mobile service based in Albany with no storefront, which means our entire day is
              built around routing crews efficiently through the region. Send your address and we'll
              confirm coverage immediately — most quotes come back the same day, and most jobs are
              scheduled within a week during the season.
            </p>
            <p>
              Because we can measure most homes remotely from satellite imagery, you rarely need to be
              home for the estimate or the work. As long as we have access to an outside spigot and
              unlocked gates, we'll clean, send before-and-after photos, and follow up the same day.
            </p>
            <p>
              Seasonality matters up here. April and May are for washing off winter salt, road grime,
              and pollen. Mid-summer is prime time for decks, patios, and paver sealing while surfaces
              stay dry long enough to cure. October and November are for gutter cleaning after leaf
              drop and for sealing concrete before the freeze-thaw cycle starts pulling it apart. We
              book those windows fast, so earlier is better.
            </p>
            <p>
              Looking for something specific in your town? Browse{" "}
              <a href="/services" className="text-[var(--brand-deep-blue)] font-semibold hover:text-[var(--brand-bright-blue)]">
                all of our services
              </a>
              , check our{" "}
              <a href="/pressure-washing-near-me" className="text-[var(--brand-deep-blue)] font-semibold hover:text-[var(--brand-bright-blue)]">
                pressure washing near me
              </a>{" "}
              hub for town-by-town pages, or{" "}
              <a href="/contact" className="text-[var(--brand-deep-blue)] font-semibold hover:text-[var(--brand-bright-blue)]">
                contact us
              </a>{" "}
              and we'll point you in the right direction.
            </p>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="bg-white py-14 md:py-16">
        <div className="mx-auto max-w-[900px] px-4">
          <h2
            className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight text-center mb-8"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Service Area Questions
          </h2>
          <div className="space-y-3">
            {FAQS.map((f) => (
              <details
                key={f.q}
                className="group rounded-lg border border-gray-200 bg-[var(--brand-light-gray)] p-5"
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-3 text-[var(--brand-deep-blue)] font-semibold">
                  <span>{f.q}</span>
                  <ChevronRight
                    size={18}
                    className="shrink-0 mt-1 transition-transform group-open:rotate-90 text-[var(--brand-red)]"
                  />
                </summary>
                <p className="mt-3 text-[var(--brand-charcoal)]/80 text-sm md:text-base leading-relaxed">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[var(--brand-deep-blue)] text-white py-12">
        <div className="mx-auto max-w-[1200px] px-4 text-center">
          <h2
            className="text-2xl md:text-3xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Are You In Our Service Area?
          </h2>
          <p className="mt-2 text-white/85">
            Send us your address and we'll confirm — plus give you a free quote for your project.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => openQuoteModal()}
              className="inline-flex items-center justify-center rounded-md bg-[#C8102E] text-white px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Get Free Quote
            </button>
            <a
              href="tel:+15189001913"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-[var(--brand-deep-blue)] px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              <Phone size={18} strokeWidth={2.5} />
              Call (518) 900-1913
            </a>
          </div>
        </div>
      </section>
      <InlineQuoteSection source="Service Area" />
    </main>
  );
}
